import { useState, useEffect, useRef } from 'react';

function useReveal(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, visible };
}

// 左侧帷幕 — 斜向左上方拉开
function CurtainLeft({ open }: { open: boolean }) {
  return (
    <div
      className="absolute inset-y-0 left-0 z-20 pointer-events-none"
      style={{
        width: '52%',
        transform: open ? 'translate(-115%, -22%)' : 'translate(0%, 0%)',
        transition: 'transform 1.2s cubic-bezier(0.76, 0, 0.24, 1) 0.2s',
        transformOrigin: 'top left',
      }}
    >
      {/* 主帷幕布料 — 深酒红，低饱和度 */}
      <div
        className="absolute inset-0"
        style={{
          background: `linear-gradient(
            108deg,
            #2a0d0d 0%,
            #3d1414 6%,
            #4a1a1a 11%,
            #3a1010 17%,
            #4e1c1c 23%,
            #3c1212 29%,
            #451818 35%,
            #521e1e 41%,
            #3e1313 47%,
            #491919 53%,
            #351010 59%,
            #441616 65%,
            #3b1111 71%,
            #4d1b1b 77%,
            #391010 83%,
            #421515 89%,
            #2e0e0e 100%
          )`,
        }}
      />
      {/* 丝绒光泽层 — 模拟布料高光 */}
      <div
        className="absolute inset-0"
        style={{
          background: `linear-gradient(
            95deg,
            rgba(255,220,200,0.04) 0%,
            rgba(255,255,255,0.0) 15%,
            rgba(255,200,180,0.06) 28%,
            rgba(0,0,0,0.0) 40%,
            rgba(255,220,200,0.03) 55%,
            rgba(0,0,0,0.0) 68%,
            rgba(255,210,190,0.05) 82%,
            rgba(0,0,0,0.0) 94%,
            rgba(255,220,200,0.02) 100%
          )`,
        }}
      />
      {/* 细密褶皱阴影 */}
      <div
        className="absolute inset-0"
        style={{
          background: `repeating-linear-gradient(
            90deg,
            rgba(0,0,0,0.42) 0px,
            rgba(0,0,0,0.1) 6px,
            rgba(0,0,0,0) 12px,
            rgba(255,255,255,0.025) 17px,
            rgba(0,0,0,0) 22px,
            rgba(0,0,0,0.3) 30px,
            rgba(0,0,0,0.08) 36px,
            rgba(0,0,0,0) 42px,
            rgba(255,255,255,0.02) 48px,
            rgba(0,0,0,0) 54px,
            rgba(0,0,0,0.38) 64px,
            rgba(0,0,0,0.1) 70px,
            rgba(0,0,0,0) 76px,
            rgba(255,255,255,0.03) 83px,
            rgba(0,0,0,0) 90px,
            rgba(0,0,0,0.25) 100px,
            rgba(0,0,0,0) 110px,
            rgba(0,0,0,0.35) 122px,
            rgba(0,0,0,0) 130px
          )`,
        }}
      />
      {/* 右侧边缘暗部（增加立体感） */}
      <div
        className="absolute top-0 bottom-0 right-0"
        style={{
          width: '60px',
          background: 'linear-gradient(90deg, transparent, rgba(0,0,0,0.55))',
        }}
      />
      {/* 右侧古铜金边 */}
      <div
        className="absolute top-0 bottom-0 right-0"
        style={{
          width: '12px',
          background: 'linear-gradient(90deg, #5a4010 0%, #8a6828 35%, #a07c32 55%, #7a5c20 75%, #4e3810 100%)',
          boxShadow: '0 0 14px rgba(140,104,40,0.45), inset -1px 0 3px rgba(255,220,120,0.15)',
        }}
      />
      {/* 顶部横梁 */}
      <div
        className="absolute top-0 left-0 right-0"
        style={{
          height: '24px',
          background: 'linear-gradient(180deg, #3a2808 0%, #6a4e18 35%, #8a6828 55%, #6a4e18 75%, #3a2808 100%)',
          boxShadow: '0 3px 12px rgba(0,0,0,0.7)',
        }}
      />
      {/* 顶部褶皱聚拢 */}
      <div
        className="absolute left-0 right-0"
        style={{
          top: '24px',
          height: '80px',
          background: `repeating-linear-gradient(
            90deg,
            rgba(0,0,0,0.5) 0px,
            rgba(0,0,0,0.15) 5px,
            rgba(0,0,0,0) 10px,
            rgba(255,255,255,0.03) 14px,
            rgba(0,0,0,0) 18px,
            rgba(0,0,0,0.4) 26px,
            rgba(0,0,0,0) 34px,
            rgba(0,0,0,0.3) 44px,
            rgba(0,0,0,0) 52px
          )`,
          maskImage: 'linear-gradient(180deg, rgba(0,0,0,0.9) 0%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(180deg, rgba(0,0,0,0.9) 0%, transparent 100%)',
        }}
      />
      {/* 底部自然垂坠阴影 */}
      <div
        className="absolute bottom-0 left-0 right-0"
        style={{
          height: '120px',
          background: 'linear-gradient(0deg, rgba(0,0,0,0.5) 0%, transparent 100%)',
        }}
      />
    </div>
  );
}

// 右侧帷幕 — 斜向右上方拉开
function CurtainRight({ open }: { open: boolean }) {
  return (
    <div
      className="absolute inset-y-0 right-0 z-20 pointer-events-none"
      style={{
        width: '52%',
        transform: open ? 'translate(115%, -22%)' : 'translate(0%, 0%)',
        transition: 'transform 1.2s cubic-bezier(0.76, 0, 0.24, 1) 0.2s',
        transformOrigin: 'top right',
      }}
    >
      {/* 主帷幕布料 */}
      <div
        className="absolute inset-0"
        style={{
          background: `linear-gradient(
            72deg,
            #2e0e0e 0%,
            #421515 6%,
            #391010 12%,
            #4d1b1b 18%,
            #3b1111 24%,
            #441616 30%,
            #351010 36%,
            #491919 42%,
            #3e1313 48%,
            #521e1e 54%,
            #451818 60%,
            #3c1212 66%,
            #4e1c1c 72%,
            #3a1010 78%,
            #4a1a1a 84%,
            #3d1414 90%,
            #2a0d0d 100%
          )`,
        }}
      />
      {/* 丝绒光泽层 */}
      <div
        className="absolute inset-0"
        style={{
          background: `linear-gradient(
            85deg,
            rgba(0,0,0,0) 0%,
            rgba(255,210,190,0.05) 18%,
            rgba(0,0,0,0) 32%,
            rgba(255,200,180,0.06) 45%,
            rgba(0,0,0,0) 60%,
            rgba(255,220,200,0.04) 74%,
            rgba(0,0,0,0) 88%,
            rgba(255,210,190,0.03) 100%
          )`,
        }}
      />
      {/* 细密褶皱阴影 */}
      <div
        className="absolute inset-0"
        style={{
          background: `repeating-linear-gradient(
            90deg,
            rgba(0,0,0,0) 0px,
            rgba(0,0,0,0.35) 8px,
            rgba(0,0,0,0.08) 14px,
            rgba(0,0,0,0) 20px,
            rgba(255,255,255,0.02) 26px,
            rgba(0,0,0,0) 28px,
            rgba(0,0,0,0.4) 38px,
            rgba(0,0,0,0.12) 44px,
            rgba(0,0,0,0) 50px,
            rgba(0,0,0,0.35) 60px,
            rgba(0,0,0,0) 68px
          )`,
        }}
      />
      {/* 左侧边缘暗部 */}
      <div
        className="absolute top-0 bottom-0 left-0"
        style={{
          width: '60px',
          background: 'linear-gradient(270deg, transparent, rgba(0,0,0,0.55))',
        }}
      />
      {/* 左侧古铜金边 */}
      <div
        className="absolute top-0 bottom-0 left-0"
        style={{
          width: '12px',
          background: 'linear-gradient(90deg, #4e3810 0%, #7a5c20 25%, #a07c32 45%, #8a6828 65%, #5a4010 100%)',
          boxShadow: '0 0 14px rgba(140,104,40,0.45), inset 1px 0 3px rgba(255,220,120,0.15)',
        }}
      />
      {/* 顶部横梁 */}
      <div
        className="absolute top-0 left-0 right-0"
        style={{
          height: '24px',
          background: 'linear-gradient(180deg, #3a2808 0%, #6a4e18 35%, #8a6828 55%, #6a4e18 75%, #3a2808 100%)',
          boxShadow: '0 3px 12px rgba(0,0,0,0.7)',
        }}
      />
      {/* 顶部褶皱聚拢 */}
      <div
        className="absolute left-0 right-0"
        style={{
          top: '24px',
          height: '80px',
          background: `repeating-linear-gradient(
            90deg,
            rgba(0,0,0,0) 0px,
            rgba(0,0,0,0.3) 8px,
            rgba(0,0,0,0) 16px,
            rgba(255,255,255,0.03) 22px,
            rgba(0,0,0,0) 28px,
            rgba(0,0,0,0.45) 38px,
            rgba(0,0,0,0.12) 44px,
            rgba(0,0,0,0) 50px,
            rgba(0,0,0,0.35) 60px,
            rgba(0,0,0,0) 68px
          )`,
          maskImage: 'linear-gradient(180deg, rgba(0,0,0,0.9) 0%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(180deg, rgba(0,0,0,0.9) 0%, transparent 100%)',
        }}
      />
      {/* 底部自然垂坠阴影 */}
      <div
        className="absolute bottom-0 left-0 right-0"
        style={{
          height: '120px',
          background: 'linear-gradient(0deg, rgba(0,0,0,0.5) 0%, transparent 100%)',
        }}
      />
    </div>
  );
}

// 顶部帷幕横幅
function CurtainValance({ open }: { open: boolean }) {
  return (
    <div
      className="absolute top-0 left-0 right-0 z-30 pointer-events-none"
      style={{ height: '80px' }}
    >
      {/* 主横幅 — 深酒红 */}
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(180deg, #1e0808 0%, #2e1010 35%, #3a1414 60%, #280c0c 85%, #1a0707 100%)',
          boxShadow: '0 6px 28px rgba(0,0,0,0.8)',
        }}
      />
      {/* 横幅细密褶皱 */}
      <div
        className="absolute inset-0"
        style={{
          background: `repeating-linear-gradient(
            90deg,
            rgba(0,0,0,0.35) 0px,
            rgba(0,0,0,0.08) 8px,
            rgba(0,0,0,0) 15px,
            rgba(255,255,255,0.02) 22px,
            rgba(0,0,0,0) 28px,
            rgba(0,0,0,0.28) 38px,
            rgba(0,0,0,0) 48px,
            rgba(0,0,0,0.22) 60px,
            rgba(0,0,0,0) 72px
          )`,
        }}
      />
      {/* 底部古铜金边条 */}
      <div
        className="absolute bottom-0 left-0 right-0"
        style={{
          height: '10px',
          background: 'linear-gradient(90deg, #3a2808, #6a4e18 12%, #8a6828 25%, #6a4e18 38%, #9a7830 50%, #6a4e18 62%, #8a6828 75%, #6a4e18 88%, #3a2808)',
          boxShadow: '0 0 12px rgba(120,88,30,0.4)',
        }}
      />
      {/* 底部流苏 */}
      <div
        className="absolute bottom-0 left-0 right-0 flex justify-around"
        style={{ transform: 'translateY(100%)' }}
      >
        {Array.from({ length: 32 }).map((_, i) => (
          <div
            key={i}
            style={{
              width: '3px',
              height: open ? '0px' : '30px',
              background: 'linear-gradient(180deg, #7a5c20, #9a7830 45%, #5a4010)',
              borderRadius: '0 0 2px 2px',
              transition: `height 0.6s ease ${1.0 + i * 0.015}s`,
              opacity: 0.85,
            }}
          />
        ))}
      </div>
      {/* 中央徽章装饰 */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center gap-4"
        style={{
          opacity: open ? 0 : 0.7,
          transition: 'opacity 0.4s ease 0.1s',
        }}
      >
        <div style={{ width: '50px', height: '1px', background: 'linear-gradient(90deg, transparent, #8a6828)' }} />
        <div
          style={{
            width: '22px',
            height: '22px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, #c8a84a 0%, #8a6828 55%, #4e3810 100%)',
            boxShadow: '0 0 8px rgba(120,88,30,0.5)',
          }}
        />
        <div style={{ width: '50px', height: '1px', background: 'linear-gradient(90deg, #8a6828, transparent)' }} />
      </div>
    </div>
  );
}

export default function VideosSection({ curtainOpen }: { curtainOpen?: boolean }) {
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);
  const [internalCurtainOpen, setInternalCurtainOpen] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const { ref: headerRef, visible: headerVisible } = useReveal(0.2);
  const { ref: cardsRef, visible: cardsVisible } = useReveal(0.1);

  // 如果外部传入 curtainOpen，则使用外部控制；否则用内部 IntersectionObserver 兜底
  const isCurtainOpen = curtainOpen !== undefined ? curtainOpen : internalCurtainOpen;

  useEffect(() => {
    if (curtainOpen !== undefined) return; // 外部控制时不启用内部观察
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setInternalCurtainOpen(true), 600);
          observer.disconnect();
        }
      },
      { threshold: 0.25 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [curtainOpen]);

  const videos = [
    {
      title: 'Batter my Heart — Doctor Atomic',
      venue: 'Manhattan, New York City',
      date: 'August 2025',
      thumbnail: 'https://img.youtube.com/vi/gPNQilx687U/maxresdefault.jpg',
      videoUrl: 'https://www.youtube.com/embed/gPNQilx687U'
    },
    {
      title: 'War, Sure I Know — Lysistrata',
      venue: 'Gerald W. Lynch Theater',
      date: 'August 2024',
      thumbnail: 'https://img.youtube.com/vi/Arp2s7xrAwE/maxresdefault.jpg',
      videoUrl: 'https://www.youtube.com/embed/Arp2s7xrAwE'
    },
    {
      title: 'Votre Toast — Carmen',
      venue: 'Trento, Italy',
      date: 'September 2024',
      thumbnail: 'https://img.youtube.com/vi/Vrl5pp195m8/hqdefault.jpg',
      videoUrl: 'https://www.youtube.com/embed/Vrl5pp195m8'
    },
    {
      title: 'E fra quest\'ansie — I Pagliacci',
      venue: 'Manhattan, New York City',
      date: 'August 2025',
      thumbnail: 'https://img.youtube.com/vi/-evgn7Y485w/maxresdefault.jpg',
      videoUrl: 'https://www.youtube.com/embed/-evgn7Y485w'
    },
    {
      title: 'È sogno? O realtà… — Falstaff',
      venue: 'Opera America National Opera Center, New York City',
      date: 'May 2025',
      thumbnail: 'https://img.youtube.com/vi/8iX3d7ol1X8/maxresdefault.jpg',
      videoUrl: 'https://www.youtube.com/embed/8iX3d7ol1X8'
    },
    {
      title: 'Когда бы жизнь домашним кругом — Eugene Onegin',
      venue: 'Manhattan, New York City',
      date: 'March 2025',
      thumbnail: 'https://img.youtube.com/vi/PeULJLsPDNA/maxresdefault.jpg',
      videoUrl: 'https://www.youtube.com/embed/PeULJLsPDNA'
    }
  ];

  return (
    <section ref={sectionRef} id="videos" className="relative overflow-hidden py-32 px-6 bg-opera-black">

      {/* 顶部渐变：与 Bernstein Quote 区平滑衔接 */}
      <div
        className="absolute top-0 left-0 right-0 z-[1] pointer-events-none"
        style={{
          height: '140px',
          background: 'linear-gradient(to bottom, #0a0a0a 0%, transparent 100%)',
        }}
      />
      {/* 底部渐变：与 Testimonials 区平滑衔接 */}
      <div
        className="absolute bottom-0 left-0 right-0 z-[1] pointer-events-none"
        style={{
          height: '140px',
          background: 'linear-gradient(to top, #0a0a0a 0%, transparent 100%)',
        }}
      />

      {/* ── 带幕层 ── */}
      <CurtainValance open={isCurtainOpen} />
      <CurtainLeft open={isCurtainOpen} />
      <CurtainRight open={isCurtainOpen} />

      {/* 带幕关闭时的中央遮罩 */}
      <div
        className="absolute inset-0 z-10 pointer-events-none"
        style={{
          background: '#120404',
          opacity: isCurtainOpen ? 0 : 1,
          transition: 'opacity 0.4s ease 0.2s',
        }}
      />

      {/* 带幕打开后的舞台聚光灯效果 */}
      <div
        className="absolute inset-0 z-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 70% 60% at 50% 40%, rgba(201,168,76,0.05) 0%, transparent 70%)',
          opacity: isCurtainOpen ? 1 : 0,
          transition: 'opacity 1.0s ease 1.2s',
        }}
      />

      <div
        className="relative z-10 max-w-7xl mx-auto"
        style={{
          opacity: isCurtainOpen ? 1 : 0,
          transition: 'opacity 0.8s ease 1.0s',
        }}
      >
        {/* Header */}
        <div ref={headerRef} className="flex flex-col md:flex-row items-start justify-between mb-10 md:mb-16 gap-4 md:gap-0">
          <div className="max-w-xl overflow-hidden">
            <h2 className="text-2xl md:text-5xl font-cinzel-decorative font-bold leading-tight mb-4 md:mb-6" style={{ color: '#c9a84c', textShadow: '0 2px 12px rgba(0,0,0,0.8), 0 1px 4px rgba(0,0,0,0.6)' }}>
              <span
                className="block"
                style={{
                  opacity: headerVisible ? 1 : 0,
                  transform: headerVisible ? 'translateX(0px)' : 'translateX(-60px)',
                  transition: 'opacity 1.1s cubic-bezier(0.22,1,0.36,1) 0s, transform 1.1s cubic-bezier(0.22,1,0.36,1) 0s',
                }}
              >
                On
              </span>
              <span
                className="block"
                style={{
                  opacity: headerVisible ? 1 : 0,
                  transform: headerVisible ? 'translateX(0px)' : 'translateX(-60px)',
                  transition: 'opacity 1.1s cubic-bezier(0.22,1,0.36,1) 0.22s, transform 1.1s cubic-bezier(0.22,1,0.36,1) 0.22s',
                }}
              >
                Stage
              </span>
            </h2>
          </div>

          <div
            className="max-w-md md:pt-8"
            style={{
              opacity: headerVisible ? 0.7 : 0,
              transform: headerVisible ? 'translateX(0px)' : 'translateX(40px)',
              transition: 'opacity 1.2s cubic-bezier(0.22,1,0.36,1) 0.45s, transform 1.2s cubic-bezier(0.22,1,0.36,1) 0.45s',
            }}
          >
            <p className="font-cormorant text-sm md:text-base leading-relaxed" style={{ color: '#ede8d8', textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>
              Selected Performance Recordings: Highlights from Opera stages, Concerts and Studio sessions.
            </p>
          </div>
        </div>

        {/* Video Grid */}
        <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-8">
          {videos.map((video, index) => (
            <div
              key={index}
              className="rounded-xl overflow-hidden shadow-2xl transition-all duration-300 hover:scale-105 hover:shadow-[0_8px_40px_rgba(201,168,76,0.25)] group cursor-pointer"
              style={{
                background: 'rgba(255,255,255,0.07)',
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                border: '1px solid rgba(201,168,76,0.18)',
                opacity: cardsVisible ? 1 : 0,
                transform: cardsVisible ? 'translateY(0px)' : 'translateY(50px)',
                transition: `opacity 1s cubic-bezier(0.22,1,0.36,1) ${index * 0.22}s, transform 1s cubic-bezier(0.22,1,0.36,1) ${index * 0.22}s, box-shadow 0.3s ease, scale 0.3s ease`,
              }}
              onClick={() => setSelectedVideo(video.videoUrl)}
            >
              <div className="relative aspect-video overflow-hidden">
                <img
                  src={video.thumbnail}
                  alt={video.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/35 flex items-center justify-center transition-all duration-300 group-hover:bg-black/55">
                  <div
                    className="w-12 h-12 md:w-16 md:h-16 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110"
                    style={{
                      background: 'rgba(201,168,76,0.75)',
                      backdropFilter: 'blur(6px)',
                      WebkitBackdropFilter: 'blur(6px)',
                      opacity: cardsVisible ? 1 : 0,
                      transform: cardsVisible ? 'scale(1)' : 'scale(0.6)',
                      transition: `opacity 0.7s ease ${index * 0.22 + 0.5}s, transform 0.7s cubic-bezier(0.34,1.56,0.64,1) ${index * 0.22 + 0.5}s`,
                    }}
                  >
                    <i className="ri-play-fill text-black text-xl md:text-2xl ml-1"></i>
                  </div>
                </div>
              </div>

              <div className="p-4 md:p-6">
                <h3 className="text-base md:text-xl font-playfair font-semibold mb-2 md:mb-3 leading-snug transition-colors duration-300" style={{ color: '#ede8d8', textShadow: '0 1px 6px rgba(0,0,0,0.7)' }}>
                  {video.title}
                </h3>
                <div className="space-y-1 text-xs md:text-sm font-cormorant" style={{ color: '#ede8d8', opacity: 0.6, textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}>
                  <p className="flex items-center gap-2">
                    <i className="ri-map-pin-line text-emerald-green flex-shrink-0"></i>
                    <span className="truncate">{video.venue}</span>
                  </p>
                  <p className="flex items-center gap-2">
                    <i className="ri-calendar-line text-emerald-green flex-shrink-0"></i>
                    {video.date}
                  </p>
                </div>
                <button
                  className="mt-3 md:mt-4 w-full px-4 py-2 md:px-6 md:py-2.5 rounded-full font-cormorant text-xs md:text-sm transition-all duration-300 whitespace-nowrap cursor-pointer"
                  style={{
                    background: 'rgba(255,255,255,0.08)',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                    border: '1px solid rgba(201,168,76,0.25)',
                    color: '#ede8d8',
                  }}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLButtonElement).style.background = 'rgba(201,168,76,0.25)';
                    (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(201,168,76,0.6)';
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.08)';
                    (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(201,168,76,0.25)';
                  }}
                >
                  Watch Full Performance
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* YouTube Channel Link */}
        <div
          className="flex justify-center mt-12 md:mt-16"
          style={{
            opacity: cardsVisible ? 1 : 0,
            transform: cardsVisible ? 'translateY(0px)' : 'translateY(24px)',
            transition: 'opacity 1s cubic-bezier(0.22,1,0.36,1) 1.4s, transform 1s cubic-bezier(0.22,1,0.36,1) 1.4s',
          }}
        >
          <a
            href="https://www.youtube.com/@VicVII"
            target="_blank"
            rel="nofollow noopener noreferrer"
            className="inline-flex items-center gap-3 px-7 py-3 rounded-full font-cormorant text-sm md:text-base tracking-widest transition-all duration-300 cursor-pointer whitespace-nowrap group"
            style={{
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(201,168,76,0.3)',
              color: '#ede8d8',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(201,168,76,0.15)';
              (e.currentTarget as HTMLAnchorElement).style.borderColor = 'rgba(201,168,76,0.6)';
              (e.currentTarget as HTMLAnchorElement).style.color = '#c9a84c';
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(255,255,255,0.06)';
              (e.currentTarget as HTMLAnchorElement).style.borderColor = 'rgba(201,168,76,0.3)';
              (e.currentTarget as HTMLAnchorElement).style.color = '#ede8d8';
            }}
          >
            <span
              className="w-7 h-7 flex items-center justify-center rounded-full transition-colors duration-300"
              style={{ background: 'rgba(201,168,76,0.18)' }}
            >
              <i className="ri-youtube-fill text-base" style={{ color: '#c9a84c' }}></i>
            </span>
            <span>Check My YouTube Channel</span>
            <i className="ri-arrow-right-up-line text-sm opacity-60 group-hover:opacity-100 transition-opacity duration-300"></i>
          </a>
        </div>

      </div>

      {/* Video Modal */}
      {selectedVideo && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-6"
          onClick={() => setSelectedVideo(null)}
        >
          <div className="relative w-full max-w-5xl aspect-video" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setSelectedVideo(null)}
              className="absolute -top-12 right-0 transition-colors duration-300 cursor-pointer"
              style={{ color: '#ede8d8' }}
            >
              <i className="ri-close-line text-3xl"></i>
            </button>
            <iframe
              src={selectedVideo}
              className="w-full h-full rounded-lg"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        </div>
      )}
    </section>
  );
}
